/**
 * Pastilles « X+ Wh » : le thème compose ce que Shopify ne sait pas exprimer.
 *
 * Les valeurs du filtre d'autonomie sont des bornes HAUTES disjointes
 * (`< 400 W` couvre 300-399). Une pastille « 300+ » n'a donc aucune valeur de
 * filtre qui lui corresponde : elle doit cocher TOUTES les cases dont le seuil
 * dépasse 300, et les décocher toutes quand on la relâche.
 *
 * HTML contract (rendu par T7b, cf. plan §T7b(b) et §T7b(d)) :
 *   <div data-topbar-panel>            -- ou .collapsible-content__inner
 *     <div class="filter-bucket-list" data-pill-compose role="group">
 *       <label class="filter-bucket-btn">
 *         <input type="radio" class="filter-bucket-btn__input" name="wh-pill"
 *                value="300" data-pill-min="300" [checked data-pill-active]>
 *         <span class="filter-bucket-btn__label">300+ Wh</span>
 *       </label>
 *       ...
 *     </div>
 *     <div class="hide" data-pill-thresholds>
 *       <input type="checkbox" name="filter.p.m.autonomie" value="..."
 *              data-threshold="400">        -- libellé de groupe, borne HAUTE
 *       <input type="checkbox" name="filter.p.m.autonomie" value="..."
 *              data-threshold-exact="712">  -- valeur brute, hors groupe
 *     </div>
 *   </div>
 *
 * Un seul attribut par case, et deux comparaisons différentes : `>` pour un
 * seuil de groupe (`< 400` est sous 400, une pastille « 400+ » ne le couvre
 * pas), `>=` pour une valeur brute (712 est bien ≥ 400).
 *
 * `[data-pill-thresholds]` est un FRÈRE de `[data-pill-compose]`, jamais un
 * descendant : la soumission émise plus bas part d'une case et doit remonter
 * jusqu'au <form>, or les deux listeners en capture étouffent tout ce qui
 * sort de `[data-pill-compose]`. Logées dans le conteneur de pastilles, les
 * cases tueraient la seule soumission du composant — panne totale, silencieuse.
 *
 * `data-pill-active` est rendu par Liquid sur la pastille active côté serveur,
 * et seulement TENU À JOUR ici : après un rendu AJAX, la source de vérité
 * redevient le Liquid. Ce n'est jamais un drapeau qui décide seul
 * (anti-pattern n°27) — l'état réel des cases fait toujours autorité.
 */
(function () {
  'use strict';

  var GROUPE = '[data-pill-compose]';
  var PASTILLE = '[data-pill-compose] input[data-pill-min]';

  function plusProche(cible, selecteur) {
    if (!cible || typeof cible.closest !== 'function') return null;
    return cible.closest(selecteur);
  }

  /**
   * Les seuils sont des entiers positifs (Liquid les rend zéro-paddés puis
   * `| plus: 0`). Tout le reste — attribut absent, vide, non numérique — vaut
   * « pas de seuil », jamais 0 : `Number('')` rend 0 et ferait couvrir la case
   * par toutes les pastilles, silencieusement.
   */
  function nombre(brut) {
    if (brut === null || typeof brut === 'undefined') return null;
    var txt = String(brut).trim();
    if (!/^\d+$/.test(txt)) return null;
    return Number(txt);
  }

  /**
   * Portée : le frère `[data-pill-thresholds]` du groupe, jamais tout le
   * document — il y a DEUX groupes dans le DOM (barre et tiroir, ou panneau et
   * tiroir), chacun avec son propre jeu de cases.
   */
  function conteneurDeSeuils(groupe) {
    var panneau = groupe.parentElement;
    if (!panneau) return null;
    return panneau.querySelector('[data-pill-thresholds]');
  }

  function casesDe(groupe) {
    var conteneur = conteneurDeSeuils(groupe);
    if (!conteneur) return [];
    return Array.prototype.slice.call(conteneur.querySelectorAll('input[type="checkbox"]'));
  }

  function pastillesDe(groupe) {
    return Array.prototype.slice.call(groupe.querySelectorAll('input[data-pill-min]'));
  }

  /** Les cases que cette pastille doit cocher — et, par différence, celles qu'elle doit décocher. */
  function thresholdsFor(groupe, pillMin) {
    if (pillMin === null) return [];
    return casesDe(groupe).filter(function (c) {
      var borneHaute = nombre(c.getAttribute('data-threshold'));
      if (borneHaute !== null) return borneHaute > pillMin;
      var exacte = nombre(c.getAttribute('data-threshold-exact'));
      return exacte !== null && exacte >= pillMin;
    });
  }

  /**
   * Garde d'ÉTAT RÉEL : la pastille est réputée appliquée seulement si les
   * cases cochées sont exactement celles qu'elle vise — égalité des deux
   * ensembles, dans les deux sens. Les valeurs d'une pastille « 800+ » sont
   * toutes couvertes par « 300+ » : une simple inclusion ferait passer les
   * deux pour actives. Même égalité que celle qu'écrit Liquid en T7b
   * (`matched_count == pill_count` ET `== filter.active_values.size`) : les
   * deux doivent rester d'accord, sans quoi un clic sur une pastille affichée
   * active la ré-appliquerait au lieu de la remettre à zéro.
   */
  function estDejaApplique(groupe, radio) {
    if (!radio.hasAttribute('data-pill-active')) return false;
    var visees = thresholdsFor(groupe, nombre(radio.getAttribute('data-pill-min')));
    var cochees = casesDe(groupe).filter(function (c) {
      return c.checked;
    });
    if (cochees.length !== visees.length) return false;
    return visees.every(function (c) {
      return c.checked;
    });
  }

  /**
   * Une seule soumission par geste, émise depuis la première case de seuil du
   * filtre : `onFormSubmit` reconstruit le FormData du <form> entier
   * (theme.js, ancre `new FormData(formEl)`), la case émettrice n'a donc pas
   * d'importance — seul compte le fait que l'event BULLE jusqu'au formulaire.
   * Dans le tiroir mobile `initFilters` ne branche rien (ancre
   * `if (form.closest('#FilterDrawer')) return`) : l'event n'y réveille
   * personne et l'état des cases est lu au clic sur « Appliquer ». C'est voulu,
   * aucun cas particulier ici.
   */
  function emettre(cases) {
    var source = null;
    for (var i = 0; i < cases.length; i++) {
      if (cases[i].hasAttribute('data-threshold')) {
        source = cases[i];
        break;
      }
    }
    if (source === null && cases.length > 0) source = cases[0];
    if (source === null) return;
    source.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Écrit l'état visé (`radioVise === null` = remise à zéro) et n'émet que si
   * le DOM a réellement bougé : ouvrir puis refermer un panneau, ou rattraper
   * un état déjà juste, ne doit pas relancer un rendu AJAX pour rien.
   */
  function synchroniser(groupe, radioVise) {
    var visees = radioVise === null ? [] : thresholdsFor(groupe, nombre(radioVise.getAttribute('data-pill-min')));
    var change = false;

    pastillesDe(groupe).forEach(function (r) {
      var active = r === radioVise;
      if (r.checked !== active) {
        r.checked = active;
        change = true;
      }
      if (active) r.setAttribute('data-pill-active', '');
      else r.removeAttribute('data-pill-active');
    });

    var cases = casesDe(groupe);
    cases.forEach(function (c) {
      var voulu = visees.indexOf(c) !== -1;
      if (c.checked !== voulu) {
        c.checked = voulu;
        change = true;
      }
    });

    if (!change) return;
    emettre(cases);
  }

  function appliquer(groupe, radio) {
    synchroniser(groupe, radio);
  }

  function remettreAZero(groupe) {
    synchroniser(groupe, null);
  }

  /**
   * Point d'entrée unique des deux chemins (souris et clavier).
   *
   * Le verrou neutralise la double émission d'une MÊME activation : `click` et
   * `change` partent dans la même tâche, donc avant le `setTimeout(…, 0)` qui
   * le libère, et le second est ignoré quel que soit son ordre d'arrivée. Deux
   * gestes réellement distincts sont toujours séparés d'au moins un tour de
   * boucle et sont tous deux traités.
   *
   * C'est `estDejaApplique` qui choisit la BRANCHE, pas seulement le contenu
   * de la branche : le composant est correct quel que soit l'état de départ.
   */
  var verrou = null;

  function traiter(groupe, radio) {
    if (verrou === radio) return;
    verrou = radio;
    setTimeout(function () {
      if (verrou === radio) verrou = null;
    }, 0);

    // Les deux ruptures du contrat de markup (T7b(d)) que rien d'autre ne
    // signalerait : sans elles, la pastille se peint active et RIEN ne se
    // passe — pas de filtre, pas d'erreur, portes au vert. On les dit.
    var conteneur = conteneurDeSeuils(groupe);
    if (conteneur === null) {
      console.warn('[filter-pill-group] aucun [data-pill-thresholds] dans le panneau : rien à composer');
      return;
    }
    if (conteneur.closest(GROUPE) !== null) {
      // Cochées mais jamais soumises : l'input remonterait de l'intérieur du
      // groupe, où le listener en capture l'étouffe. On compose quand même —
      // le tiroir mobile, lui, lit les cases au clic sur « Appliquer ».
      console.warn(
        '[filter-pill-group] [data-pill-thresholds] doit être le FRÈRE de [data-pill-compose], pas son enfant',
      );
    }

    if (estDejaApplique(groupe, radio)) {
      remettreAZero(groupe);
    } else {
      appliquer(groupe, radio);
    }
  }

  /**
   * 1. `click`, et non `input` : un radio DÉJÀ coché ne réémet ni `input` ni
   * `change` (règle 6 du plan), or « re-cliquer la pastille active la remet à
   * zéro » est une exigence. `preventDefault()` est systématique — le
   * navigateur n'effectue alors pas sa propre bascule et n'émet rien ; la
   * cochabilité est pilotée à la main.
   */
  function surClic(e) {
    var radio = plusProche(e.target, PASTILLE);
    if (!radio) return;
    e.preventDefault();
    traiter(radio.closest(GROUPE), radio);
  }

  /**
   * 2. `change` en CAPTURE : le seul chemin du clavier. Aux flèches, le
   * navigateur coche le radio suivant sans passer par `click`, et
   * `.filter-bucket-btn__input:checked + .filter-bucket-btn__label`
   * (assets/filter-bucket.css) le peint aussitôt en actif. Sans ce listener la
   * pastille serait visuellement sélectionnée et fonctionnellement morte :
   * aucune case cochée, aucun filtre appliqué. `change` n'étant pas annulable,
   * on ne peut pas empêcher le navigateur de cocher — on RATTRAPE l'état.
   * `stopPropagation()` parce que l'event brut n'a rien à faire au niveau du
   * formulaire : la seule soumission du composant est celle émise plus haut.
   */
  function surChangement(e) {
    var radio = plusProche(e.target, PASTILLE);
    if (!radio) return;
    e.stopPropagation();
    traiter(radio.closest(GROUPE), radio);
  }

  /**
   * 3. `input` en CAPTURE, sans aucune logique : il empêche la frappe clavier
   * brute de réveiller `onFormSubmit`, branché en `input` sur le <form>
   * (theme.js, ancre `form.addEventListener('input'`). Le scope
   * `[data-pill-compose]` est PORTANT : la soumission du composant part d'une
   * case de `[data-pill-thresholds]`, frère et non descendant du groupe — son
   * `closest()` est donc nul, elle n'est pas étouffée et remonte jusqu'au
   * formulaire.
   */
  function surSaisie(e) {
    if (!plusProche(e.target, GROUPE)) return;
    e.stopPropagation();
  }

  document.addEventListener('click', surClic);
  document.addEventListener('change', surChangement, true);
  document.addEventListener('input', surSaisie, true);
})();
